import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function SkyHighViewsVancouver() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "sky-high-views-in-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext() || { articles: [] };
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Vancouver Lookout (Harbour Centre Tower)",
    "section-2": "Grouse Mountain",
    "section-3": "Cypress Mountain Highview Lookout",
    "section-4": "Queen Elizabeth Park",
    "section-5": "Lonsdale Quay & The Shipyards",
    "section-6": "Prospect Point, Stanley Park",
    "section-7": "Burnaby Mountain (Kamui Mintara)",
    "section-8": "Canada Place & Convention Centre",
    "local-regulations": "Local Regulations & Best Practices",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://vancouver.ca/parks-recreation-culture/stanley-park.aspx",
      label: "Stanley Park (Official City Info)",
    },
    {
      url: "https://vancouver.ca/parks-recreation-culture/queen-elizabeth-park.aspx",
      label: "Queen Elizabeth Park (City of Vancouver)",
    },
    {
      url: "https://www.grousemountain.com/",
      label: "Grouse Mountain (Official Site)",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // Each location is matched to a relevant section with coordinates from the conversation.
  const locations = [
    {
      id: "section-1",
      lat: 49.28462,
      lng: -123.11232,
      popupText: "Vancouver Lookout (Harbour Centre Tower)",
    },
    {
      id: "section-2",
      lat: 49.37956,
      lng: -123.0818,
      popupText: "Grouse Mountain",
    },
    {
      id: "section-3",
      lat: 49.36258,
      lng: -123.18800,
      popupText: "Cypress Mountain Highview Lookout",
    },
    {
      id: "section-4",
      lat: 49.24222,
      lng: -123.11500,
      popupText: "Queen Elizabeth Park",
    },
    {
      id: "section-5",
      lat: 49.31043,
      lng: -123.08278,
      popupText: "Lonsdale Quay & The Shipyards",
    },
    {
      id: "section-6",
      lat: 49.31417,
      lng: -123.14139,
      popupText: "Prospect Point (Stanley Park)",
    },
    {
      id: "section-7",
      lat: 49.28288,
      lng: -122.93472,
      popupText: "Burnaby Mountain (Kamui Mintara)",
    },
    {
      id: "section-8",
      lat: 49.28891,
      lng: -123.11101,
      popupText: "Canada Place & Convention Centre",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Sky-High Views in Vancouver: Best Lookout Points</title>
        <meta
          name="description"
          content="Discover top vantage points in Vancouver, including Grouse Mountain, Cypress Lookout, and more. Get coordinates, tips, and FAQs to plan your next view-hunting adventure."
        />
        <meta
          name="keywords"
          content="Vancouver viewpoints, skyline views, Grouse Mountain, Cypress, Queen Elizabeth Park"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/sky-high-views-in-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Sky-High Views in Vancouver: Best Lookout Points"
        />
        <meta
          property="og:description"
          content="Explore the ultimate vantage spots in Vancouver, from Harbour Centre's 360° deck to mountain peaks overlooking the city."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/sky-high-views-in-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/sky-high-views-in-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Sky-High Views in Vancouver: Best Lookout Points"
        />
        <meta
          name="twitter:description"
          content="An in-depth guide to Vancouver’s top viewpoint locations, complete with coordinates, tips, and photos."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/sky-high-views-in-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="The vibrant skyline of Vancouver with mountains in the backdrop."
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver is a city of striking natural beauty blended with
              modern urbanity. From snow-capped mountains to sparkling harbors
              and glassy skyscrapers, there’s no shortage of panoramic viewpoints
              to capture its essence. This guide highlights the city’s top
              vantage points—whether you’re a photographer seeking that perfect
              shot or a traveler wanting a 360° skyline view. Read on for
              coordinates, tips on ideal visiting times, approximate costs, and
              other essential details for each location.
            </p>

            {/* SECTION 1 */}
            <h2 id="section-1">
              Vancouver Lookout (Harbour Centre Tower)
            </h2>
            <p>
              Perched 168 m (553 ft) above downtown, the Vancouver Lookout
              offers an iconic 360° observation deck. Expect breathtaking views
              of Stanley Park, Gastown, and the North Shore Mountains.
              A high-speed elevator gets you to the top in just 40 seconds.
            </p>
            <p
              ref={(el) => setSpotRef("section-1", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Coordinates:</strong>{" "}
              <Marker
                coordY={49.28462}
                coordX={-123.11232}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – Admission is around CAD $18–$20 for adults, valid for multiple
              entries on the same day. Accessible by public transit (near
              Waterfront Station).
            </p>

            {/* SECTION 2 */}
            <h2 id="section-2">Grouse Mountain</h2>
            <p>
              Nicknamed the “Peak of Vancouver,” Grouse Mountain offers a
              spectacular vantage point of the city and beyond. Ride the Skyride
              gondola for sweeping panoramas, or hike the famous Grouse Grind
              trail if you’re up for a challenge. At 1,250 m elevation, it’s
              cooler up top, so dress accordingly. 
            </p>
            <p
              ref={(el) => setSpotRef("section-2", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Coordinates:</strong>{" "}
              <Marker
                coordY={49.37956}
                coordX={-123.0818}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – Gondola tickets range from CAD $20–$60, depending on the season
              and activities. Public transit bus routes connect from downtown to
              the mountain base.
            </p>

            {/* SECTION 3 */}
            <h2 id="section-3">Cypress Mountain Highview Lookout</h2>
            <p>
              The Highview Lookout on Cypress Bowl Road offers one of the most
              expansive panoramas of Vancouver’s skyline. Photographers often
              arrive at dawn to capture an ethereal city-in-the-clouds effect.
              It’s a short drive from downtown (30 minutes), but public transit
              doesn’t run directly to the lookout.
            </p>
            <p
              ref={(el) => setSpotRef("section-3", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Coordinates:</strong>{" "}
              <Marker
                coordY={49.36258}
                coordX={-123.188}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – Free roadside parking. In winter, check road conditions and
              carry chains if required.
            </p>

            {/* SECTION 4 */}
            <h2 id="section-4">Queen Elizabeth Park</h2>
            <p>
              As Vancouver’s highest point within city limits (125 m above sea
              level), Queen Elizabeth Park serves up a delightful combo of lush
              gardens and skyline views. Stroll the manicured grounds and head
              near the Bloedel Conservatory for the best perspective of downtown
              framed by the North Shore Mountains.
            </p>
            <p
              ref={(el) => setSpotRef("section-4", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Coordinates:</strong>{" "}
              <Marker
                coordY={49.24222}
                coordX={-123.115}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – Free entry to the park itself; paid parking on-site. Easily
              reached by car or public transit (SkyTrain + short bus ride).
            </p>

            {/* SECTION 5 */}
            <h2 id="section-5">Lonsdale Quay & The Shipyards</h2>
            <p>
              Situated on the North Vancouver waterfront, Lonsdale Quay is
              perfect for a postcard view of downtown Vancouver across Burrard
              Inlet. Stroll the pier in The Shipyards District or enjoy food
              stalls in the public market. Sunset and nighttime views are
              particularly stunning here.
            </p>
            <p
              ref={(el) => setSpotRef("section-5", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Coordinates:</strong>{" "}
              <Marker
                coordY={49.31043}
                coordX={-123.08278}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – Accessible via the SeaBus from Waterfront Station (around CAD
              $3–$4). No entry fee to enjoy the views.
            </p>

            {/* SECTION 6 */}
            <h2 id="section-6">Prospect Point, Stanley Park</h2>
            <p>
              Located at the northern tip of Stanley Park, Prospect Point
              provides a classic perspective of Lions Gate Bridge with West
              Vancouver in the distance. A small café and observation deck make
              it a popular stop. Be mindful of park hours and potential parking
              fees.
            </p>
            <p
              ref={(el) => setSpotRef("section-6", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Coordinates:</strong>{" "}
              <Marker
                coordY={49.31417}
                coordX={-123.14139}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – Free to visit; pay parking is required. Bicycle or drive through
              Stanley Park’s loop road.
            </p>

            {/* SECTION 7 */}
            <h2 id="section-7">Burnaby Mountain (Kamui Mintara)</h2>
            <p>
              High above the city to the east, Burnaby Mountain boasts sweeping
              vistas of the Vancouver skyline backed by coastal mountains. The
              “Kamui Mintara” (Playground of the Gods) sculptures add a unique
              artistic foreground. Perfect for sunset photography.
            </p>
            <p
              ref={(el) => setSpotRef("section-7", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Coordinates:</strong>{" "}
              <Marker
                coordY={49.28288}
                coordX={-122.93472}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – Free parking and entry. Accessible by car (30 minutes from
              downtown) or transit to SFU, then a short walk.
            </p>

            {/* SECTION 8 */}
            <h2 id="section-8">Canada Place & Vancouver Convention Centre</h2>
            <p>
              You don’t always need to climb for a great view. Canada Place and
              the Convention Centre area offer a waterfront perspective of the
              North Shore Mountains, Stanley Park, and bustling harbor traffic.
              Stroll along the promenade or watch seaplanes come and go.
            </p>
            <p
              ref={(el) => setSpotRef("section-8", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Coordinates:</strong>{" "}
              <Marker
                coordY={49.28891}
                coordX={-123.11101}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – Free to explore on foot. A short walk from Waterfront Station.
            </p>

            {/* SECTION: Local Regulations & Best Practices */}
            <h2 id="local-regulations">Local Regulations & Best Practices</h2>
            <p>
              When enjoying these viewpoints, remember to follow local bylaws:
              keep to marked trails, avoid trespassing, and do not climb over
              fences or railings for photos. Drone usage is restricted in most
              city parks. In winter, mountain roads (Cypress, Grouse) may
              require snow tires or chains. Always check weather conditions,
              dress appropriately, and respect wildlife. If photographing with
              a tripod, be mindful of crowds.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Do I need a car to reach all these viewpoints?</h3>
              <p>
                Not necessarily. Many spots, like the Vancouver Lookout and
                Canada Place, are downtown and easily accessible by transit.
                Lonsdale Quay can be reached via the SeaBus. Grouse Mountain has
                a bus route to the base. Cypress Mountain Lookout is easiest by
                car, and Burnaby Mountain is also more convenient with a vehicle
                (or a bus to SFU, then a walk).
              </p>

              <h3>2. Which is the best time of day for a skyline photo?</h3>
              <p>
                Golden hours—sunrise and sunset—offer the most dramatic lighting.
                Evening captures city lights, while midday on a clear day gives
                sharp mountain detail. Each viewpoint has its own magic, so
                choose based on your schedule and weather conditions.
              </p>

              <h3>3. Are these viewpoints open year-round?</h3>
              <p>
                Yes. All are accessible year-round, but winter conditions may
                affect road access (especially Cypress and Grouse). Check for
                any seasonal closures or special event restrictions.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Whether you’re a visitor or a local, discovering Vancouver’s beauty
              from above adds a new dimension to your appreciation of the city.
              From the urban heart of Canada Place to the mountain peaks of
              Grouse and Cypress, each vantage point offers a unique perspective.
              Now, gear up, check the weather, and embark on your next
              “sky-high” journey in Vancouver!
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}